import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import { useAuth } from "../context/AuthContext";

const FaqManagement = () => {
  const { accessToken } = useAuth();
  const baseURL = process.env.REACT_APP_BASEURL;

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  const [feedback, setFeedback] = useState({
    open: false,
    type: "",
    message: "",
  });

  // Fetch FAQs
  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${baseURL}/faq`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setFaqs(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) fetchFaqs();
  }, [accessToken]);

  const handleOpen = (faq = null) => {
    setEditingFaq(faq);

    if (faq) {
      setFormData({
        question: faq.question,
        answer: faq.answer,
      });
    } else {
      setFormData({ question: "", answer: "" });
    }

    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingFaq(null);
  };

  // Save FAQ (Create or Update)
  const handleSubmit = async () => {
    if (!formData.question || !formData.answer) {
      setFeedback({
        open: true,
        type: "error",
        message: "Please fill all fields",
      });
      return;
    }

    try {
      if (editingFaq) {
        await axios.patch(
          `${baseURL}/faq/${editingFaq._id}`,
          formData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setFeedback({
          open: true,
          type: "success",
          message: "FAQ Updated Successfully",
        });
      } else {
        await axios.post(
          `${baseURL}/faq`,
          formData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setFeedback({
          open: true,
          type: "success",
          message: "FAQ Created Successfully",
        });
      }

      handleClose();
      fetchFaqs();
    } catch (err) {
      setFeedback({
        open: true,
        type: "error",
        message: "Operation failed",
      });
    }
  };

  // Delete FAQ
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      await axios.delete(`${baseURL}/faq/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setFeedback({
        open: true,
        type: "success",
        message: "FAQ Deleted Successfully",
      });

      fetchFaqs();
    } catch {
      setFeedback({
        open: true,
        type: "error",
        message: "Delete Failed",
      });
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: 4 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
        FAQ Management
      </Typography>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">FAQs</Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Add FAQ
            </Button>
          </Box>

          {loading ? (
            <Box textAlign="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Answer</TableCell>
                  <TableCell width={120}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {faqs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No FAQs Found
                    </TableCell>
                  </TableRow>
                )}

                {faqs.map((faq) => (
                  <TableRow key={faq._id}>
                    <TableCell>{faq.question}</TableCell>
                    <TableCell>{faq.answer}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpen(faq)}>
                        <EditIcon color="primary" />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(faq._id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingFaq ? "Edit FAQ" : "Add FAQ"}
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Question"
            fullWidth
            margin="normal"
            name="question"
            value={formData.question}
            onChange={(e) =>
              setFormData({ ...formData, question: e.target.value })
            }
          />

          <TextField
            label="Answer"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            name="answer"
            value={formData.answer}
            onChange={(e) =>
              setFormData({ ...formData, answer: e.target.value })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" onClick={handleSubmit}>
            {editingFaq ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
      >
        <Alert severity={feedback.type}>{feedback.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FaqManagement;
